/**
 * Contact Timeline — infinite-scroll chronological feed of all events.
 */

import { useCallback } from 'react'
import { useCrmStore } from '@/stores/crmStore'
import { useCRM } from '@/hooks/useCRM'
import { TimelineEventRow } from '@/components/crm/TimelineEventRow'
import { EmptyState, Skeleton } from '@/components/ui'
import { Clock } from 'lucide-react'

interface ContactTimelineProps {
  contactId: string
}

export function ContactTimeline({ contactId }: ContactTimelineProps) {
  const events = useCrmStore((s) => s.timelineEvents)
  const hasMore = useCrmStore((s) => s.timelineHasMore)
  const nextCursor = useCrmStore((s) => s.timelineNextCursor)
  const isLoading = useCrmStore((s) => s.isLoadingTimeline)
  const { fetchTimeline } = useCRM()

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading || !nextCursor) return
    fetchTimeline(contactId, { before: nextCursor })
  }, [contactId, hasMore, isLoading, nextCursor, fetchTimeline])

  // Group events by day
  const groupedEvents = groupByDay(events.map((e) => ({ ...e, date: new Date(e.createdAt) })))

  if (isLoading && events.length === 0) {
    return (
      <div className="px-6 py-4 space-y-2">
        <Skeleton className="h-16 rounded-md" />
        <Skeleton className="h-16 rounded-md" />
        <Skeleton className="h-16 rounded-md" />
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <EmptyState
        icon={Clock}
        title="No activity yet"
        description="Events will appear here as interactions occur"
      />
    )
  }

  return (
    <div className="px-6 py-4">
      {groupedEvents.map(([label, dayEvents]) => (
        <div key={label} className="mb-6">
          {/* Day separator */}
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {label}
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Events */}
          <div className="space-y-2">
            {dayEvents.map((event) => (
              <TimelineEventRow key={event.id} event={event} />
            ))}
          </div>
        </div>
      ))}

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center py-4">
          <button
            onClick={loadMore}
            disabled={isLoading}
            className="text-sm text-primary hover:text-primary/80 disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────

interface EventWithDate {
  date: Date
  [key: string]: unknown
}

function groupByDay<T extends EventWithDate>(events: T[]): Array<[string, T[]]> {
  const groups = new Map<string, T[]>()
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  for (const event of events) {
    const date = event.date
    let label: string

    if (isSameDay(date, today)) {
      label = 'Today'
    } else if (isSameDay(date, yesterday)) {
      label = 'Yesterday'
    } else {
      label = date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      })
    }

    const existing = groups.get(label)
    if (existing) {
      existing.push(event)
    } else {
      groups.set(label, [event])
    }
  }

  return Array.from(groups.entries())
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}
